import { useFragment } from "@/generated";
import {
  DuplicateProjectDocument,
  GetProjectByParentIdDocument,
  LessonFieldsFragmentDoc,
  ProjectFieldsFragmentDoc,
  SlideFieldsFragmentDoc,
} from "@/generated/graphql";
import type { ProjectFieldsFragment } from "@/generated/graphql";
import { getProjectsByParentIdVars } from "@/lib/utils/graphql/getProjectsByParentIdVars";
import { saveScrollTarget } from "@/lib/utils/scrollRestoration";
import { withToast } from "@/lib/utils/withToast";
import type { ApolloCache } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { useNavigate } from "@tanstack/react-router";
import { updateDuplicateLessonCache } from "../lessons/useDuplicateLesson";

function groupByKey<T>(items: T[], getKey: (item: T) => string | null): Map<string | null, T[]> {
  const map = new Map<string | null, T[]>();

  for (const item of items) {
    const key = getKey(item);
    const group = map.get(key) ?? [];
    group.push(item);
    map.set(key, group);
  }

  return map;
}

/**
 * Hook for duplicating a project with all its children, updating the Apollo cache.
 *
 * @example
 * const { duplicateProject, loading } = useDuplicateProject();
 * const newProject = await duplicateProject(
 *   "project123",
 *   "parentProject456",
 *   "My Duplicated Project",
 *   "linked"
 * );
 */
function updateProjectsCache(
  cache: ApolloCache,
  projectsByParentIdMap: Map<string | null, ProjectFieldsFragment[]>,
  targetParentId?: string | null
) {
  for (const [parentId, projects] of projectsByParentIdMap.entries()) {
    // for highest level project : update parent cache to add this new item
    if (parentId === targetParentId) {
      const project = projects[0];
      if (!project) continue;

      const id = cache.identify(project);
      if (id) {
        cache.writeFragment({
          id,
          fragment: ProjectFieldsFragmentDoc,
          data: project,
        });
      }

      const newRefs = { __ref: id };

      cache.modify({
        id: "ROOT_QUERY",
        fields: {
          projectsByParentId(existingData, { storeFieldName }) {
            const key = parentId === null ? `"parentId":null` : `"parentId":"${parentId}"`;
            const isTrashKey = '"isTrash":false';

            if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
              return existingData;

            return {
              ...existingData,
              items: [newRefs, ...existingData.items],
              total: (existingData.total || 0) + 1,
            };
          },
        },
      });

      if (parentId !== null) {
        cache.modify({
          id: `Project:${parentId}`,
          fields: {
            itemCount(existingData = 0) {
              return existingData + 1;
            },
          },
        });
      }
    } else {
      // for child level project : write new query
      const activeProjects = projects.filter((project) => !project.isTrash);

      cache.writeQuery({
        query: GetProjectByParentIdDocument,
        variables: getProjectsByParentIdVars({ parentId }),
        data: {
          projectsByParentId: {
            hasMore: false,
            items: activeProjects,
            total: activeProjects.length,
          },
        },
      });
    }
  }
}

export function useDuplicateProject() {
  const navigate = useNavigate();
  const [duplicateProjectMutation, { loading }] = useMutation(DuplicateProjectDocument, {
    update(cache, { data }, { variables }) {
      if (!data?.duplicateProject) return;

      const duplicatedProjects = data.duplicateProject.projects.map((project) =>
        useFragment(ProjectFieldsFragmentDoc, project)
      );

      const duplicatedLessons = data.duplicateProject.lessons.map((lesson) =>
        useFragment(LessonFieldsFragmentDoc, lesson)
      );

      const duplicatedSlides = data.duplicateProject.slides.map((slide) =>
        useFragment(SlideFieldsFragmentDoc, slide)
      );

      const projectsByParentId = groupByKey(duplicatedProjects, (p) => p.parentId ?? null);
      const slidesByLessonId = groupByKey(duplicatedSlides, (s) => s.lessonId ?? null);

      try {
        cache.batch({
          update(cache) {
            updateProjectsCache(cache, projectsByParentId, variables?.targetParentId);

            for (const lesson of duplicatedLessons) {
              updateDuplicateLessonCache(
                cache,
                lesson,
                slidesByLessonId.get(lesson.id) ?? [],
                variables?.duplicationType
              );
            }

            // Add to original project's linkedCopies
            if (variables?.duplicationType === "linked") {
              for (const project of duplicatedProjects) {
                if (project.parentId === null) return;
                cache.modify({
                  id: `Project:${project.originalProjectId}`,
                  fields: {
                    linkedCopies: (existingData) => {
                      return [...existingData, { id: project.id, name: project.name }];
                    },
                  },
                });
              }
            }
          },
        });
      } catch (error) {
        console.error("Duplicate projects cache update failed:", error);
      }
    },
  });

  const duplicateProject = async (
    projectId: string,
    targetParentId: string | null,
    name: string,
    duplicationType: "unique" | "linked",
    insertAfterSortOrder?: number | null
  ) => {
    return withToast(
      async () => {
        const result = await duplicateProjectMutation({
          variables: {
            projectId,
            targetParentId,
            name,
            duplicationType,
            insertAfterSortOrder,
          },
        });

        const duplicatedProjects = result.data?.duplicateProject.projects ?? [];
        const firstProject = duplicatedProjects[0]
          ? useFragment(ProjectFieldsFragmentDoc, duplicatedProjects[0])
          : null;

        // Navigate and scroll to the duplicated project
        if (firstProject) {
          const pageKey = firstProject.parentId ?? "home";
          saveScrollTarget(`project-${firstProject.id}`, pageKey);

          if (firstProject.parentId) {
            navigate({
              to: "/admin/project/$projectId",
              params: { projectId: firstProject.parentId },
            });
          } else {
            navigate({ to: "/admin" });
          }
        }
      },
      {
        loading: "Duplicating project...",
        success: `Successfully duplicated project "${name}"`,
        error: `Failed to duplicate project "${name}"`,
      },
      "duplicateProject"
    );
  };

  return {
    duplicateProject,
    loading,
  };
}
